'use strict';

const _template = require('lodash/template');
const _find = require('lodash/find');
const _invert = require('lodash/invert');
const _get = require('lodash/get');
const _has = require('lodash/has');
const _escape = require('lodash/escape');
const _includes = require('lodash/includes');
const _isPlainObject = require('lodash/isPlainObject');
const _last = require('lodash/last');
const _startsWith = require('lodash/startsWith');
const _dropRight = require('lodash/dropRight');
const _drop = require('lodash/drop');
const _join = require('lodash/join');
const _assign = require('lodash/assign');
const _reduce = require('lodash/reduce');
const _isString = require('lodash/isString');

const externalRegx = /^(http|https):\/\/(.*)/;
const mailtoRegx = /^mailto:([^\?]*)(?:\?subject=(.*)?)?/;
const pageRegx = /^\/([^ \?]*)[\?]?(.*)/;
const pageTopRegx = /^(\/?)([^ \?]*)#(top|SCROLL_TO_TOP)[\?]?(.*)/;
const pageBottomRegx = /^(\/?)([^ \?]*)#(bottom|SCROLL_TO_BOTTOM)[\?]?(.*)/;
const documentRegx = /^document:\/\/(.*)/;
const phoneRegx = /^tel:(.*)/;
const pageWithAnchor = /^\/([^ \?]*)#([^ \?]*)[\?]?(.*)/;
const samePageWithAnchor = /^#([^ \?]*)[\?]?(.*)/;
const ANCHOR_TYPE_TO_NAME = {
  SCROLL_TO_TOP: 'top',
  SCROLL_TO_BOTTOM: 'bottom'
};
const LinkType = {
  PAGE: 'PageLink',
  DYNAMIC_PAGE: 'DynamicPageLink',
  DYNAMIC_PAGE_WITH_QUERY_PARAMS: 'DynamicPageLinkWithQueryParams',
  EXTERNAL: 'ExternalLink',
  EMAIL: 'EmailLink',
  DOCUMENT: 'DocumentLink',
  PHONE: 'PhoneLink',
  PAGE_TOP: 'AnchorLink',
  PAGE_BOTTOM: 'AnchorLink',
  SAME_PAGE_ANCHOR_LINK: 'SamePageAnchorLink',
  ANCHOR: 'AnchorLink'
};

const SUPPORTED_TARGETS = ['_blank', '_self'];

const MASTER_PAGE_ID = 'masterPage';

const anchorLinkBaseTemplate = _template('/<%=linkObject.pageId%>#<%=linkObject.anchorDataId%>', {variable: 'linkObject'});
const pageLinkBaseTemplate = _template('/<%=linkObject.pageId%>', {variable: 'linkObject'});

function getInnerRoute(parts, hasAnchor, hasQueryParams) {
  let partsWithoutQueryParams = parts;
  if (hasQueryParams) {
    partsWithoutQueryParams = _dropRight(parts)
  }
  const partsWithoutPrefixAndSuffix = _drop(hasAnchor ? _dropRight(partsWithoutQueryParams) : partsWithoutQueryParams);
  return _join(partsWithoutPrefixAndSuffix, '/');
}


const LINK_URL_TEMPLATES = {
  PageLink: {
    url: getPageLinkTemplate, link(type, parts) {
      const pageLinkObj = {
        type,
        pageId: '#' + parts[1]

      };
      if (parts.length === 3 && parts[2]) {
        _assign(pageLinkObj, {queryParams: convertQueryParamsToObject(parts[2]) || ''})
      }
      return pageLinkObj;
    }, regex: [pageRegx]
  },
  DocumentLink: {
    url: _template('document://<%=linkObject.docId%>', {variable: 'linkObject'}), link(type, parts) {
      return {
        type,
        docId: parts[1]
      };
    }, regex: [documentRegx]
  },
  PhoneLink: {
    url: _template('tel:<%=linkObject.phoneNumber%>', {variable: 'linkObject'}), link(type, parts) {
      return {
        type,
        phoneNumber: parts[1]
      };
    }, regex: [phoneRegx]
  },
  EmailLink: {
    url: getEmailLinkTemplate,
    link(type, parts) {
      const data = {
        type,
        recipient: _escape(parts[1])
      };
      if (parts[2]) {
        data.subject = _escape(parts[2]);
      }
      return data;
    },
    regex: [mailtoRegx]
  },
  ExternalLink: {
    url: _template('<%=linkObject.url%>', {variable: 'linkObject'}), link(type, parts, routersMap, target) {
      if (target && !_includes(SUPPORTED_TARGETS, target)) {
        throw new Error('Unsupported target value');
      }
      return {
        type,
        url: _template('<%= partA %>://<%= partB%>')({partA: parts[1], partB: parts[2]}),
        target: target || '_blank'
      };
    }, regex: [externalRegx]
  },
  DynamicPageLink: {
    url: getDynamicPageLinkTemplate, link(type, parts, siteStructurePrefixes) {
      const hasAnchor = _startsWith(_last(parts), '#');
      const routerData = _find(siteStructurePrefixes, {name: parts[0]});
      const data = {
        type,
        innerRoute: getInnerRoute(parts, hasAnchor) || '/',
        routerId: routerData.id
      };
      if (hasAnchor) {
        const anchorName = _last(parts).replace('#', '');
        data.anchorDataId = _get(_invert(ANCHOR_TYPE_TO_NAME), anchorName, anchorName);
      }
      return data;
    }, regex: [pageTopRegx, pageBottomRegx, pageWithAnchor, pageRegx]
  },
  DynamicPageLinkWithQueryParams: {
    url: getDynamicPageLinkTemplate, link(type, parts, siteStructurePrefixes) {
      const queryParams = _last(parts)
      const hasAnchor = _startsWith(parts[parts.length - 2], '#');
      const routerData = _find(siteStructurePrefixes, {name: parts[0]});
      const data = {
        type: LinkType.DYNAMIC_PAGE,
        innerRoute: getInnerRoute(parts, hasAnchor, true) || '/',
        routerId: routerData.id,
        queryParams: convertQueryParamsToObject(queryParams)

      };
      if (hasAnchor) {
        const anchorName = parts[parts.length - 2].replace('#', '');
        data.anchorDataId = _get(_invert(ANCHOR_TYPE_TO_NAME), anchorName, anchorName);
      }
      return data;
    }, regex: [pageTopRegx, pageBottomRegx, pageWithAnchor, pageRegx]
  },
  AnchorLink: {
    url: getAnchorLinkTemplate, link(type, parts) {
      const anchorDataId = parts[2];
      const anchorLinkObj = {
        type,
        pageId: '#' + parts[1],
        anchorDataId: _invert(ANCHOR_TYPE_TO_NAME)[anchorDataId] || anchorDataId
      };
      if (parts.length === 4 && parts[3]) {
        _assign(anchorLinkObj, {queryParams: convertQueryParamsToObject(parts[3])})
      }
      return anchorLinkObj
    }, regex: [pageWithAnchor]
  },
  SamePageAnchorLink: {
    url: getAnchorLinkTemplate, link(type, parts) {
      const anchorDataId = parts[1];
      const samePageAnchorLinkObj = {
        type: LinkType.ANCHOR,
        anchorDataId: _invert(ANCHOR_TYPE_TO_NAME)[anchorDataId] || anchorDataId,
        pageId: '#' + MASTER_PAGE_ID
      };
      if (parts.length === 3 && parts[2]) {
        _assign(samePageAnchorLinkObj, {queryParams: convertQueryParamsToObject(parts[2])})
      }
      return samePageAnchorLinkObj
    }, regex: [samePageWithAnchor]
  }
};

function getUrlParts(regExpArr, value, type) {
  if (type === LinkType.DYNAMIC_PAGE || type === LinkType.DYNAMIC_PAGE_WITH_QUERY_PARAMS) {
    return getDynamicPageParts(value);
  }
  const regexp = _find(regExpArr, function (regExpItem) {
    return regExpItem.test(value);
  });
  let parts = value.match(regexp);
  parts = parts ? parts : [];
  return parts;
}

function getDynamicPageParts(str) {
  const pageRegRes = str.match(pageRegx);
  const parts = pageRegRes[1].replace('#', '/#').split(/[\/]+/);
  const hasQueryParam = _includes(str, '?');
  if (hasQueryParam) {
    parts.push(_last(pageRegRes))
  }
  return parts;
}

function getAnchorName(anchorDataId) {
  return ANCHOR_TYPE_TO_NAME[anchorDataId] || anchorDataId;
}

function getDynamicPagePrefix(siteStructurePrefixes, linkObject) {
  const routerConfig = _find(siteStructurePrefixes, {id: linkObject.routerId});
  return routerConfig && routerConfig.prefix;
}

function getDynamicPageLinkTemplate(linkObject, siteStructurePrefixes) {
  const dynamicPageSuffix = !linkObject.innerRoute || linkObject.innerRoute === '/' ? '' : '/' + linkObject.innerRoute;
  const customLinkObject = {
    pageId: getDynamicPagePrefix(siteStructurePrefixes, linkObject).replace('/','') + dynamicPageSuffix,
    anchorDataId: getAnchorName(linkObject.anchorDataId)
  };
  return customLinkObject.anchorDataId ? anchorLinkBaseTemplate(customLinkObject) : LINK_URL_TEMPLATES.PageLink.url(customLinkObject); //eslint-disable-line new-cap
}

function getPageLinkTemplate(linkObject) {
  return pageLinkBaseTemplate({
    pageId: getPageIdForURL(linkObject.pageId)
  });
}

function getEmailLinkTemplate(linkObject) {
  let templateString = 'mailto:<%=linkObject.recipient%>';
  if (_has(linkObject, 'subject')) {
    templateString += '?subject=<%=linkObject.subject%>';
  }

  return _template(templateString, {variable: 'linkObject'})(linkObject);
}

function getAnchorLinkTemplate(linkObject) {
  const anchorName = getAnchorName(linkObject.anchorDataId);

  if (isMasterPage(extractPageId(linkObject.pageId))) {
    return '#' + anchorName;
  }

  return anchorLinkBaseTemplate({
    pageId: getPageIdForURL(linkObject.pageId),
    anchorDataId: anchorName
  });
}

function getPageIdForURL(pageId) {
  pageId = extractPageId(pageId);
  if (isMasterPage(pageId)) {
    return '';
  }

  return pageId.replace('#', '');
}

function extractPageId(pageId) {
  if (!pageId) {
    return pageId;
  }

  if (_isPlainObject(pageId)) {
    if (pageId.pageUriSEO) {
      return pageId.pageUriSEO;
    } else if (isMasterPage(pageId.id)) {
      return MASTER_PAGE_ID;
    }

    throw new Error('pageId object should contain pageUriSEO');
  }

  return pageId;
}

function isDynamicPage(urlParts, siteStructurePrefixes) {
  return _find(siteStructurePrefixes, {name: urlParts[0]});
}

function getDynamicPageType(str) {
  return _includes(str, '?') ? LinkType.DYNAMIC_PAGE_WITH_QUERY_PARAMS : LinkType.DYNAMIC_PAGE;
}

function isMasterPage(pageId) {
  return pageId.replace('#', '') === MASTER_PAGE_ID;
}

function getLinkType(str, siteStructurePrefixes) {
  switch (true) {
  case externalRegx.test(str):
    return LinkType.EXTERNAL;
  case mailtoRegx.test(str):
    return LinkType.EMAIL;
  case samePageWithAnchor.test(str):
    return LinkType.SAME_PAGE_ANCHOR_LINK;
  case pageTopRegx.test(str):
    return isDynamicPage(getDynamicPageParts(str), siteStructurePrefixes) ? getDynamicPageType(str) : LinkType.PAGE_TOP;
  case pageBottomRegx.test(str):
    return isDynamicPage(getDynamicPageParts(str), siteStructurePrefixes) ? getDynamicPageType(str) : LinkType.PAGE_BOTTOM;
  case pageWithAnchor.test(str):
    return isDynamicPage(getDynamicPageParts(str), siteStructurePrefixes) ? getDynamicPageType(str) : LinkType.ANCHOR;
  case pageRegx.test(str):
    return isDynamicPage(getDynamicPageParts(str), siteStructurePrefixes) ? getDynamicPageType(str) : LinkType.PAGE;
  case documentRegx.test(str):
    return LinkType.DOCUMENT;
  case phoneRegx.test(str):
    return LinkType.PHONE;
  default:
    throw new Error('Unsupported link type');
  }
}

function convertLinkObjectToUrl(siteStructurePrefixes, linkObject) {
  if (!linkObject || !_get(LINK_URL_TEMPLATES, [linkObject.type, 'url'])) {
    throw new Error('Provided link type is not supported');
  }
  const urlTemplate = _get(LINK_URL_TEMPLATES, [linkObject.type, 'url']);
  return urlTemplate(linkObject, siteStructurePrefixes);
}

function convertUrlToLinkObject(siteStructurePrefixes, url, target) {
  const type = getLinkType(url, siteStructurePrefixes);
  const urlParts = getUrlParts(LINK_URL_TEMPLATES[type].regex, url, type);
  const linkTemplate = _get(LINK_URL_TEMPLATES, [type, 'link']);
  return linkTemplate(type, urlParts, siteStructurePrefixes, target);
}

function getAnchorDataId(linkItem) {
  return getAnchorName(linkItem.anchorDataId);
}

function getPageId(linkItem) {
  return extractPageId(linkItem.pageId);
}

function convertQueryParamsToObject(query) {
  if (!_isString(query)) {
    return {};
  }
  const queryStringArray = query.split('&');
  return _reduce(queryStringArray, function (res, queryParam) {
    const splitedQueryParam = queryParam.split("=");
    res[splitedQueryParam[0]] = splitedQueryParam[1] ? decodeURIComponent(splitedQueryParam[1]) : null;
    return res;
  }, {});
};

module.exports = {
  convertLinkObjectToUrl,
  convertUrlToLinkObject,
  getAnchorDataId,
  getPageId,
  convertQueryParamsToObject
};



// WEBPACK FOOTER //
// ./src/linkUtils.js